import React, { createContext, useState, useEffect, useContext } from 'react';

import pb from '../lib/pocketbase';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState(() => {
        // Load from local storage on initial render
        const savedCart = localStorage.getItem('cart');
        return savedCart ? JSON.parse(savedCart) : [];
    });

    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isClickCollectActive, setIsClickCollectActive] = useState(true);

    useEffect(() => {
        // Save to local storage whenever cart changes
        localStorage.setItem('cart', JSON.stringify(cartItems));
    }, [cartItems]);

    // Check settings on mount
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const records = await pb.collection('settings').getList(1, 1);
                if (records.items.length > 0) {
                    setIsClickCollectActive(records.items[0].clickCollectActive);
                }
            } catch (err) {
                // Ignore auto-cancellation errors
                if (!err.isAbort) {
                    console.log("Settings fetch error, using default", err);
                }
            }
        };
        fetchSettings();
    }, []);

    const addToCart = (product) => {
        if (!isClickCollectActive) {
            alert("Désolé, le Click & Collect est temporairement désactivé.");
            return;
        }

        setCartItems((prevItems) => {
            // Check if item already exists
            const existingItem = prevItems.find((item) => item.id === product.id);
            const currentQuantityInCart = existingItem ? existingItem.quantity : 0;

            // Check stock availability (if stock property exists)
            if (product.stock !== undefined && (currentQuantityInCart + 1) > product.stock) {
                alert(`Désolé, nous n'avons que ${product.stock} exemplaires de "${product.name}" disponibles.`);
                return prevItems;
            }

            if (existingItem) {
                return prevItems.map((item) =>
                    item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            return [...prevItems, { ...product, quantity: 1 }];
        });
        setIsCartOpen(true); // Open cart sidebar/modal when item is added
    };

    const removeFromCart = (productId) => {
        setCartItems((prevItems) => prevItems.filter((item) => item.id !== productId));
    };

    const updateQuantity = (productId, newQuantity) => {
        if (newQuantity < 1) {
            removeFromCart(productId);
            return;
        }
        setCartItems((prevItems) =>
            prevItems.map((item) => {
                if (item.id === productId) {
                    // Check stock if available
                    if (item.stock !== undefined && newQuantity > item.stock) {
                        alert(`Désolé, il n'y a que ${item.stock} exemplaires disponibles.`);
                        return item;
                    }
                    return { ...item, quantity: newQuantity };
                }
                return item;
            })
        );
    };

    const clearCart = () => {
        setCartItems([]);
    };

    const cartTotal = cartItems.reduce((total, item) => total + (item.prix * item.quantity), 0);
    const cartCount = cartItems.reduce((count, item) => count + item.quantity, 0);

    return (
        <CartContext.Provider
            value={{
                cartItems,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearCart,
                cartTotal,
                cartCount,
                isCartOpen,
                setIsCartOpen,
                isClickCollectActive
            }}
        >
            {children}
        </CartContext.Provider>
    );
};
